import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Activity, Gauge, ListOrdered, Wallet } from "lucide-react";
import { Sparkline } from "@/lovable-demo/components/charts";
import { RiskBadge } from "@/lovable-demo/components/bits";
import { fmtCompactCurrency, fmtNumber } from "@/lovable-demo/lib/warranty/calc";
import { portfolio, recordsForProgram, topFive } from "@/lovable-demo/lib/warranty/aggregate";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";
import { SignOutButton } from "@/lovable-demo/components/account-bar";

export const Route = createFileRoute("/demo/programs")({
  head: () => ({
    meta: [
      { title: "Broadview Warranty Intelligence — Select Program" },
      {
        name: "description",
        content:
          "Choose a vehicle program to open its closed-loop warranty intelligence: governed Problem Records, Top 5 risk, exposure and prevention.",
      },
      { property: "og:title", content: "Broadview Warranty Intelligence — Select Program" },
      {
        property: "og:description",
        content:
          "Choose a vehicle program to open its closed-loop warranty intelligence: governed Problem Records, Top 5 risk, exposure and prevention.",
      },
    ],
  }),
  component: ProgramSelection,
});

function ProgramSelection() {
  const { data, setActiveProgram } = useWarranty();
  const navigate = useNavigate();

  const open = (programId: string) => {
    setActiveProgram(programId);
    void navigate({ to: "/demo/dashboard" });
  };

  return (
    <div className="min-h-screen grid-backdrop">
      <div className="mx-auto w-full max-w-6xl px-6 py-14">
        <div className="mb-6 flex justify-end">
          <SignOutButton />
        </div>
        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.3em] text-primary">
          Broadview
        </p>
        <h1 className="mt-3 max-w-3xl text-4xl font-semibold leading-tight sm:text-5xl">
          Broadview — an evidence-governed decision platform
        </h1>
        <p className="mt-4 max-w-2xl text-base text-muted-foreground">
          Every field signal terminates in one governed Problem Record where the 8D, PFMEA and
          control plan live as chapters — evidence-gated, clause-mapped and financially quantified.
          Select a program to enter.
        </p>

        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {data.programs.map((p) => {
            const records = recordsForProgram(data, p.id);
            const stats = portfolio(records);
            const worst = topFive(records)[0];
            const above = p.c1000 > p.c1000Target;
            return (
              <button
                key={p.id}
                onClick={() => open(p.id)}
                className="group rounded-lg border border-border bg-card p-5 text-left panel-glow transition-colors hover:border-primary/60"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="num text-[0.7rem] uppercase tracking-[0.2em] text-primary">
                      {p.code} · MY{p.modelYear}
                    </p>
                    <h2 className="mt-1.5 text-lg font-semibold">{p.name}</h2>
                    <p className="text-sm text-muted-foreground">
                      {p.vehicleLine} · {p.region}
                    </p>
                  </div>
                  <div className="h-10 w-28 shrink-0">
                    <Sparkline data={p.trend} tone={above ? "2" : "4"} />
                  </div>
                </div>

                <dl className="mt-5 grid grid-cols-4 gap-3 border-t border-border pt-4 text-sm">
                  <div>
                    <dt className="text-[0.62rem] uppercase tracking-wider text-muted-foreground">
                      C/1000
                    </dt>
                    <dd className={`num font-semibold ${above ? "text-warning" : "text-success"}`}>
                      {p.c1000.toFixed(1)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[0.62rem] uppercase tracking-wider text-muted-foreground">
                      Units
                    </dt>
                    <dd className="num font-semibold">{fmtNumber(p.unitsInService)}</dd>
                  </div>
                  <div>
                    <dt className="text-[0.62rem] uppercase tracking-wider text-muted-foreground">
                      Open PRs
                    </dt>
                    <dd className="num font-semibold">{stats.open}</dd>
                  </div>
                  <div>
                    <dt className="text-[0.62rem] uppercase tracking-wider text-muted-foreground">
                      Exposure
                    </dt>
                    <dd className="num font-semibold">{fmtCompactCurrency(stats.exposure)}</dd>
                  </div>
                </dl>

                {worst && (
                  <p className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                    <RiskBadge risk={worst.risk} score={worst.riskScore} />
                    <span className="truncate">{worst.title}</span>
                  </p>
                )}
                <p className="mt-4 flex items-center gap-1.5 text-sm font-medium text-primary opacity-0 transition-opacity group-hover:opacity-100">
                  Open program <ArrowRight className="size-4" />
                </p>
              </button>
            );
          })}
        </div>

        <div className="mt-10">
          <p className="text-[0.66rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Fast shortcuts — all programs
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {[
              { to: "/demo/dashboard", label: "Executive dashboard", icon: Gauge },
              { to: "/demo/top5", label: "Top 5 intelligence", icon: Activity },
              { to: "/demo/problem-records", label: "Problem record registry", icon: ListOrdered },
              { to: "/demo/financial", label: "Financial exposure", icon: Wallet },
            ].map((s) => (
              <Link
                key={s.to}
                to={s.to}
                onClick={() => setActiveProgram(null)}
                className="inline-flex items-center gap-2 rounded border border-border bg-card px-3 py-2 text-sm transition-colors hover:border-primary/60 hover:text-primary"
              >
                <s.icon className="size-4" />
                {s.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
